import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Chip, Link } from "@mui/material";
import { pageTitle, lead, h2Rule, paragraph, ctaSx, stepBadge, fadeUp } from "../theme/marketingStyles";

/** /hoe-het-werkt — volledige uitleg van de flow: 4 stappen (verticale rail,
 *  zelfde beeldtaal als de homepage), wat je nodig hebt, de videosessie
 *  praktisch, en annuleren/herroepen (kort, met link naar de voorwaarden).
 *  Eerlijke claims: "meestal binnen 48 uur", nooit een garantie. */
export default function HowItWorks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const steps = [1, 2, 3, 4].map((n) => ({ n, title: t(`howItWorks.step${n}Title`), body: t(`howItWorks.step${n}Body`) }));

  const bullets = (keys: string[]) => (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
      {keys.map((k) => (
        <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
      ))}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 6, sm: 9 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ textAlign: { xs: "center", md: "left" }, ...fadeUp(0) }}>
          <Typography variant="h1" sx={pageTitle}>{t("howItWorks.title")}</Typography>
          <Typography sx={{ ...lead, maxWidth: 600, mx: { xs: "auto", md: 0 } }}>{t("howItWorks.subtitle")}</Typography>
        </Box>

        {/* Verticale stappenrail — zelfde badge + verbindingslijn als de
            homepage; groen markeert het gratis moment (stap 3). */}
        <Box sx={{ mb: 6, ...fadeUp(120) }}>
          {steps.map((s, i) => (
            <Box key={s.n} sx={{ display: "flex", gap: { xs: 2.5, sm: 3 }, alignItems: "stretch" }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={stepBadge}>{s.n}</Box>
                {i < steps.length - 1 && <Box sx={{ width: "1px", flexGrow: 1, bgcolor: "var(--neutral-300)", my: 1 }} />}
              </Box>
              <Box sx={{ pb: i < steps.length - 1 ? 4 : 0, pt: 0.75 }}>
                <Typography variant="h3" sx={{ fontSize: "1.25rem", mb: 0.5 }}>
                  {s.title}
                  {s.n === 3 && (
                    <Chip label={t("landing.stepFreeBadge")} size="small" sx={{ ml: 1.25, bgcolor: "var(--success)", color: "#fff", fontWeight: 600, fontSize: ".72rem", height: 22, verticalAlign: "text-bottom" }} />
                  )}
                </Typography>
                <Typography sx={{ m: 0, color: "text.secondary", lineHeight: 1.7 }}>{s.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
          <Button variant="contained" size="large" onClick={() => navigate("/start")} sx={{ minWidth: 240, ...ctaSx }}>
            {t("howItWorks.cta")}
          </Button>
        </Box>

        {/* Stap ná de match: zelfhulp + verdiepende trajecten die de
            psycholoog kan toewijzen. */}
        <Typography variant="h2" sx={h2Rule}>{t("howItWorks.supportTitle")}</Typography>
        <Typography sx={paragraph}>
          {t("howItWorks.support1")}{" "}
          <Link component={RouterLink} to="/zelfhulp">{t("nav.selfHelp")}</Link>
        </Typography>

        <Typography variant="h2" sx={h2Rule}>{t("howItWorks.needTitle")}</Typography>
        <Typography sx={paragraph}>{t("howItWorks.needIntro")}</Typography>
        {bullets(["howItWorks.need1", "howItWorks.need2", "howItWorks.need3", "howItWorks.need4"])}

        <Typography variant="h2" sx={h2Rule}>{t("howItWorks.videoTitle")}</Typography>
        <Typography sx={paragraph}>{t("howItWorks.video1")}</Typography>
        <Typography sx={paragraph}>{t("howItWorks.video2")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("howItWorks.cancelTitle")}</Typography>
        <Typography sx={paragraph}>{t("howItWorks.cancel1")}</Typography>
        <Typography sx={paragraph}>
          {t("howItWorks.cancel2")}{" "}
          <Link component={RouterLink} to="/voorwaarden">{t("howItWorks.cancelLink")}</Link>
        </Typography>

        <Typography variant="h2" sx={h2Rule}>{t("howItWorks.moreTitle")}</Typography>
        <Typography sx={paragraph}>
          {t("howItWorks.more1")}{" "}
          <Link component={RouterLink} to="/hoe-matchen-wij">{t("howItWorks.moreMatchLink")}</Link>
          {" · "}
          <Link component={RouterLink} to="/terugbetaling">{t("howItWorks.moreReimbLink")}</Link>
          {" · "}
          <Link component={RouterLink} to="/veelgestelde-vragen">{t("howItWorks.moreFaqLink")}</Link>
        </Typography>
      </Container>
    </Box>
  );
}
